import { useEcosystem } from "#clockwise/web-commons/src/util/ecosystem";
import { getAttendeesPainText } from "@clockwise/client-commons/src/util/scheduling-slot";
import { ExpandLess, ExpandMore, MeetingRoom } from "@clockwise/design-system/icons";
import {
  EcosystemEnum,
  Inconveniences,
  SchedulingAttendanceSummaryTag,
  SchedulingMove,
  SchedulingRoomImpact,
  Unavailabilities,
} from "@clockwise/schema";
import classNames from "classnames";
import { MagicWand } from "phosphor-react";
import React, { CSSProperties } from "react";
import { AttendeesPains, AttendeesPainsCompact } from "../attendees-pains";
import { isMakeItWorkSlot } from "./../make-it-work/MakeItWork";
import { TagTitle } from "./TagTitle";
import { TileSubtitle } from "./TileSubtitle";
import { countImpacts } from "./utils/countImpacts";
import { getRelativeDateTime } from "./utils/date";

export type Person = {
  profile?: {
    givenName?: string | null;
    familyName?: string | null;
  } | null;
  primaryEmail: string;
  isYou: boolean;
};

export type SuggestedTimeSlot = {
  timeRange: {
    startTime: {
      dateOrDateTime: string;
    };
    endTime: {
      dateOrDateTime: string;
    } | null;
  };
  summary: {
    tags: SchedulingAttendanceSummaryTag[];
    unavailableAttendees: string[];
    focusTimeMinutesCost: number;
  };
  roomImpacts: SchedulingRoomImpact[];
  attendanceImpacts: Array<{
    calendarId: string;
    hasConflict: boolean;
    inconveniences: Inconveniences[];
    unavailabilities: Unavailabilities[];
    focusTimeMinutesCost: number;
    conflictingMeetings: string[];
  }>;
  additionalChanges?: SchedulingMove[];
};

type Props<T> = {
  slot: T;
  index: number;
  isLoading: boolean;
  isSelected: boolean;
  onSelect: (slot: T) => void;
  className?: string;
  people: Person[];
  onMouseEnter: (slot: T) => void;
  onMouseLeave: () => void;
  roomName: string;
  styleOverrides?: CSSProperties;
};

const MagicWandIcon = ({ selected }: { selected: boolean }) => (
  <MagicWand color={selected ? "white" : "#4459dd"} weight="fill" size="14px" />
);

const MakeItWorkTitleTag = ({ selected }: { selected: boolean }) => (
  <div
    className={classNames("cw-flex cw-items-center cw-gap-1 cw-mb-1 cw-font-bold", {
      "cw-text-onEmphasis": selected,
      "cw-text-[#4459dd]": !selected,
    })}
  >
    <MagicWandIcon selected={selected} />
    <span> Make it work</span>
  </div>
);

export function SuggestedTimeTileLarge<T extends SuggestedTimeSlot>({
  slot,
  index,
  isLoading,
  isSelected,
  onSelect,
  people,
  onMouseEnter,
  onMouseLeave,
  roomName,
}: Props<T>) {
  const startDate = slot.timeRange.startTime.dateOrDateTime,
    { tags, focusTimeMinutesCost } = slot.summary;
  const [numImpacts, numImpactTypes] = countImpacts(slot);

  const [isExpanded, setIsExpanded] = React.useState(false);
  const ecosystem = useEcosystem();

  const pains = (slot && people && getAttendeesPainText(slot, people, false)) || [];
  // Always trying to avoid conditionality, but want to display the pains compact if
  // we have any uncrawled calenders, otherwise too much going on in the view.
  const hasUncrawledPain = pains.find((pain) => pain.pain === Inconveniences.Uncrawled);

  const hasMultipleImpacts = numImpactTypes > 1 || hasUncrawledPain;

  const slotIsMakeItWork = isMakeItWorkSlot(slot);

  return (
    <div
      className={classNames(
        "cw-m-2 cw-border cw-rounded cw-border-interactable cw-border-solid cw-body-sm",
        "cw-transition-colors cw-ease-in cw-duration-100",
        {
          "cw-text-onEmphasis cw-bg-positive-emphasis cw-border-positive-emphasis": isSelected,
          "cw-bg-default hover:cw-border-interactable-hover cw-cursor-pointer": !isSelected,
          "cw-opacity-40": isLoading && !isSelected,
        },
      )}
      onClick={() => {
        if (!isLoading) {
          onSelect(slot);
        }
      }}
      onMouseEnter={() => onMouseEnter(slot)}
      onMouseLeave={onMouseLeave}
      cw-id="suggested-time-tile-large"
    >
      <div className="cw-p-2">
        {slotIsMakeItWork ? (
          <MakeItWorkTitleTag selected={isSelected} />
        ) : (
          <TagTitle tags={tags} index={index} isSelected={isSelected} numImpacts={numImpacts} />
        )}
        <div
          className={`cw-body-base cw-font-bold cw-py-1 ${
            isSelected ? "cw-text-onEmphasis" : "cw-text-muted"
          }`}
        >
          {getRelativeDateTime({ date: startDate })}
        </div>
        {ecosystem !== EcosystemEnum.Microsoft && roomName && (
          <div
            className={`cw-caption cw-flex cw-items-center ${
              isSelected ? "cw-text-onEmphasis" : ""
            }`}
          >
            <MeetingRoom className="cw-w-4 cw-h-4 cw-mr-1 cw-flex cw-flex-shrink-0" />
            <span>{roomName}</span>
          </div>
        )}
        <TileSubtitle
          numImpacts={numImpacts}
          focusTimeMinutesCost={focusTimeMinutesCost}
          isSelected={isSelected}
        />
      </div>
      {pains?.length > 0 && !slotIsMakeItWork && (
        <div
          className={classNames("cw-flex cw-items-center cw-justify-between cw-px-2 cw-rounded-b", {
            "cw-text-muted cw-bg-neutral": !isSelected,
            "hover:cw-bg-neutral-hover": hasMultipleImpacts && !isSelected,
            "cw-text-onEmphasis cw-bg-positive-emphasis-pressed": isSelected,
          })}
          onClick={(e) => {
            if (hasMultipleImpacts) {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }
          }}
        >
          {pains.length > 0 && hasMultipleImpacts && !isExpanded ? (
            <AttendeesPainsCompact pains={pains} renderIsSelected={isSelected} />
          ) : (
            <div className="cw-py-2">
              <AttendeesPains pains={pains} onEmphasis={isSelected} />
            </div>
          )}
          {hasMultipleImpacts && (
            <div className={`cw-w-6 cw-h-6 ${isExpanded ? "cw-self-start" : "cw-self-center"}`}>
              {isExpanded ? <ExpandLess /> : <ExpandMore />}
            </div>
          )}
        </div>
      )}
      {slotIsMakeItWork && (
        <div
          className={classNames(
            "cw-flex cw-items-center cw-justify-between cw-p-2 cw-rounded-b cw-gap-2",
            {
              "cw-bg-neutral cw-text-[#4459dd]": !isSelected,
              // "hover:cw-bg-neutral-hover": hasMultipleImpacts && !isSelected,
              "cw-bg-positive-emphasis-pressed cw-text-onEmphasis": isSelected,
            },
          )}
        >
          <div className="cw-h-[14px] cw-w-[14px">
            <MagicWandIcon selected={isSelected} />
          </div>
          <div className="cw-text-sm cw-font-light">
            Clockwise can resolve this conflict to make this time work
          </div>
        </div>
      )}
    </div>
  );
}
